import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  checkout,
  updateBookingDeposit,
  updateBookingStatus,
  type AssistantFeeEntry,
  type PaymentMethodEntry,
} from "@repo/api-client";
import type { BookingStatus } from "@repo/types";
import { qk } from "../query";

const numeric = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export type ConfirmBookingDepositInput = {
  bookingId: string;
  booking?: unknown;
  depositAmount: number;
  totalAmount?: number | string | null;
  depositPaymentMethod?: string | null;
  depositPaymentMethods?: PaymentMethodEntry[] | null;
  note?: string | null;
  transferReference?: string | null;
  paymentStatus?: "unpaid" | "partial" | "paid";
  depositPaidAt?: string | null;
  /** Skip the auto LINE booking-confirmation push for this confirm (caller sends it manually instead). */
  suppressLineNotification?: boolean;
};

export type ClearBookingDepositInput = {
  bookingId: string;
  depositAmount?: number;
};

type BookingActionOptions = {
  invalidateQueryKeys?: ReadonlyArray<readonly unknown[]>;
  getInvalidateQueryKeys?: (bookingId?: string) => ReadonlyArray<readonly unknown[]>;
  onCheckInSuccess?: () => void;
  onCheckInError?: (error: unknown) => void;
  onStatusSuccess?: (status: BookingStatus) => void;
  onStatusError?: (error: unknown) => void;
  onCheckoutSuccess?: () => void;
  onCheckoutError?: (error: unknown) => void;
  onCancelSuccess?: () => void;
  onCancelError?: (error: unknown) => void;
  onDepositSuccess?: (result: { lineNotified: boolean }) => void;
  onDepositError?: (error: unknown) => void;
  onClearDepositSuccess?: () => void;
  onClearDepositError?: (error: unknown) => void;
};

export function useBookingActions({
  invalidateQueryKeys = [],
  getInvalidateQueryKeys,
  onCheckInSuccess,
  onCheckInError,
  onStatusSuccess,
  onStatusError,
  onCheckoutSuccess,
  onCheckoutError,
  onCancelSuccess,
  onCancelError,
  onDepositSuccess,
  onDepositError,
  onClearDepositSuccess,
  onClearDepositError,
}: BookingActionOptions = {}) {
  const queryClient = useQueryClient();

  const invalidateQueries = React.useCallback(
    async (bookingId?: string) => {
      const keys = [
        qk.bookings(),
        ...(bookingId ? [qk.booking(bookingId)] : []),
        ...invalidateQueryKeys,
        ...(getInvalidateQueryKeys ? getInvalidateQueryKeys(bookingId) : []),
      ];

      const seen = new Set<string>();
      const uniqueKeys = keys.filter((key) => {
        const serialized = JSON.stringify(key);
        if (seen.has(serialized)) return false;
        seen.add(serialized);
        return true;
      });

      await Promise.all(
        uniqueKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      );
    },
    [getInvalidateQueryKeys, invalidateQueryKeys, queryClient]
  );

  const updateStatus = React.useCallback(
    async (
      id: string,
      status: BookingStatus,
      paymentMethod?: string,
      paymentMethods?: PaymentMethodEntry[]
    ) => {
      try {
        await updateBookingStatus(id, status, paymentMethod, paymentMethods);
        await invalidateQueries(id);
        onStatusSuccess?.(status);
      } catch (error) {
        onStatusError?.(error);
        throw error;
      }
    },
    [invalidateQueries, onStatusError, onStatusSuccess]
  );

  const confirmCheckout = React.useCallback(
    async (
      id: string,
      paymentMethods: PaymentMethodEntry[],
      assistantFees?: AssistantFeeEntry[],
      loyaltyPointsToUse?: number
    ) => {
      try {
        await checkout(id, {
          payment_methods: paymentMethods,
          ...(loyaltyPointsToUse && loyaltyPointsToUse > 0
            ? { loyalty_points_to_use: loyaltyPointsToUse }
            : {}),
          ...(assistantFees && assistantFees.length > 0
            ? { assistant_fees: assistantFees }
            : {}),
        });
        await invalidateQueries(id);
        onCheckoutSuccess?.();
      } catch (error) {
        onCheckoutError?.(error);
        throw error;
      }
    },
    [invalidateQueries, onCheckoutError, onCheckoutSuccess]
  );

  const cancelBooking = React.useCallback(
    async (id: string) => {
      try {
        const result = await updateBookingStatus(id, "cancelled");
        await invalidateQueries(id);
        onCancelSuccess?.();
        return result;
      } catch (error) {
        onCancelError?.(error);
        throw error;
      }
    },
    [invalidateQueries, onCancelError, onCancelSuccess]
  );

  const checkInMutation = useMutation({
    mutationFn: (id: string) => updateStatus(id, "checked_in"),
    onSuccess: () => {
      onCheckInSuccess?.();
    },
    onError: (error) => {
      onCheckInError?.(error);
    },
  });

  const confirmDepositMutation = useMutation({
    mutationFn: async ({
      bookingId,
      depositAmount,
      totalAmount,
      depositPaymentMethod,
      depositPaymentMethods,
      note,
      transferReference,
      paymentStatus,
      depositPaidAt,
      suppressLineNotification,
    }: ConfirmBookingDepositInput) =>
      updateBookingDeposit(bookingId, {
        deposit_amount: depositAmount,
        deposit_due_amount: depositAmount,
        deposit_required: true,
        deposit_paid: true,
        deposit_payment_method: depositPaymentMethod ?? null,
        deposit_payment_methods: depositPaymentMethods ?? null,
        note: note ?? null,
        transfer_reference: transferReference ?? null,
        payment_status:
          paymentStatus ??
          (depositAmount >= numeric(totalAmount) && numeric(totalAmount) > 0 ? "paid" : "partial"),
        deposit_paid_at: depositPaidAt ?? new Date().toISOString(),
        suppress_line_notification: suppressLineNotification,
      }),
    onSuccess: async (data, variables) => {
      await invalidateQueries(variables.bookingId);
      onDepositSuccess?.({ lineNotified: Boolean(data?.lineNotified) });
    },
    onError: (error) => {
      onDepositError?.(error);
    },
  });

  const clearDepositMutation = useMutation({
    mutationFn: async ({ bookingId, depositAmount = 0 }: ClearBookingDepositInput) =>
      updateBookingDeposit(bookingId, {
        deposit_amount: depositAmount,
        deposit_due_amount: depositAmount,
        deposit_paid: false,
        deposit_payment_method: null,
        deposit_payment_methods: null,
        deposit_required: true,
        deposit_paid_at: null,
        transfer_reference: null,
      }),
    onSuccess: async (_data, variables) => {
      await invalidateQueries(variables.bookingId);
      onClearDepositSuccess?.();
    },
    onError: (error) => {
      onClearDepositError?.(error);
    },
  });

  return {
    checkInMutation,
    confirmDepositMutation,
    clearDepositMutation,
    updateStatus,
    confirmCheckout,
    cancelBooking,
  };
}
